import { AfterViewInit, Directive, DoCheck, Host, Optional, Renderer2, Self, ViewContainerRef } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatButton } from '@angular/material/button';

@Directive({
  selector: '[customPaginator]',
})
export class CustomPaginatorDirective implements DoCheck, AfterViewInit {
  private currentPage: number;
  private pageGapTxt: string[];
  private rangeStart: number;
  private rangeEnd: number;
  private buttons: Array<MatButton | HTMLSpanElement> = [];
  private showTotalPages: number;
  private checkPage: number[];

  constructor(
    @Host() @Self() @Optional() private readonly matPag: MatPaginator,
    private readonly viewContainer: ViewContainerRef,
    private readonly renderer: Renderer2
  ) {
    this.currentPage = 1;
    this.pageGapTxt = ['...', '...'];
    this.showTotalPages = 5;
    this.checkPage = [0, 0, 0];

    const paginatorIntl = this.matPag._intl;
    paginatorIntl.nextPageLabel = '';
    paginatorIntl.previousPageLabel = '';
    paginatorIntl.firstPageLabel = '';
    paginatorIntl.lastPageLabel = '';
    this.matPag.hidePageSize = true;

    // Display custom range label text
    this.matPag._intl.getRangeLabel = (page: number, pageSize: number, length: number): string => {
      const startIndex = page * pageSize;
      const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
      return length > 0 ? '全' + length + '件中　' + (startIndex + 1) + '件～' + endIndex + '件表示' : '全0件中　0件～0件表示';
    };
    this.matPag._intl.itemsPerPageLabel = '';

    // Subscribe to rerender buttons when next page and last page button is used
    this.matPag.page.subscribe((paginator: PageEvent) => {
      this.currentPage = paginator.pageIndex;
      this.matPag.pageIndex = paginator.pageIndex;
      this.initPageRange();
    });
  }

  ngDoCheck(): void {
    // Reset paginator if the pageSize, pageIndex, length changes
    if (
      this.matPag?.length !== this.checkPage[0] ||
      this.matPag?.pageSize !== this.checkPage[1] ||
      this.matPag?.pageIndex !== this.checkPage[2]
    ) {
      const pageCount = this.matPag.getNumberOfPages();
      if (this.currentPage > pageCount && pageCount !== 0) {
        this.currentPage = 1;
        this.matPag.pageIndex = 0;
      }
      this.currentPage = this.matPag.pageIndex;
      this.initPageRange();
      this.checkPage = [this.matPag.length, this.matPag.pageSize, this.matPag.pageIndex];
      if (this.matPag.showFirstLastButtons) {
        setTimeout(() => {
          this.customFirstLastPageIcons();
        }, 500);
      }
    }
  }

  ngAfterViewInit(): void {
    this.rangeStart = 0;
    this.rangeEnd = this.showTotalPages - 1;
    this.initPageRange();
    this.customFirstLastPageIcons();
  }

  private customFirstLastPageIcons(): void {
    const lastBtn = this.viewContainer.element.nativeElement.querySelector('button.mat-paginator-navigation-last');
    if (lastBtn) {
      lastBtn.innerHTML = `<mat-icon class="mat-icon material-icons mat-icon-no-color">keyboard_double_arrow_right</mat-icon>`;
    }
    const firstBtn = this.viewContainer.element.nativeElement.querySelector('button.mat-paginator-navigation-first');
    if (firstBtn) {
      firstBtn.innerHTML = `<mat-icon class="mat-icon material-icons mat-icon-no-color">keyboard_double_arrow_left</mat-icon><span class="mat-button-focus-overlay"></span>`;
    }
  }

  private buildPageNumbers() {
    const totalPages: number = this.matPag.getNumberOfPages();
    const startIndex: number = Math.max(this.currentPage - this.showTotalPages, 0);

    // Container div with paginator elements
    const actionContainer = this.viewContainer.element.nativeElement.querySelector('div.mat-paginator-range-actions');
    // Button that triggers the next page action
    const nextPageNode = this.viewContainer.element.nativeElement.querySelector('button.mat-paginator-navigation-next');
    // Label showing the page range
    const pageRange = this.viewContainer.element.nativeElement.querySelector('div.mat-paginator-range-label');

    let prevButtonCount = this.buttons.length;

    // Remove buttons before creating new ones
    if (prevButtonCount > 0) {
      this.buttons.forEach(button => {
        this.renderer.removeChild(actionContainer, button);
      });
      // Empty state array
      prevButtonCount = 0;
    }

    this.renderer.addClass(pageRange, 'custom-paginator-counter');
    this.renderer.addClass(actionContainer, 'custom-paginator-container');

    // Initialize next page and last page buttons
    this.initNextAndLastButtons(prevButtonCount, actionContainer);

    this.createPageButtons(startIndex, totalPages, actionContainer, nextPageNode);
  }

  private createPageButtons(startIndex: number, totalPages: number, actionContainer: HTMLElement, nextPageNode: HTMLElement) {
    const dots: boolean[] = [false, false];
    for (let index = startIndex; index < totalPages; index++) {
      if (this.shouldShowButton(index, totalPages)) {
        this.renderer.insertBefore(actionContainer, this.createButton(index, this.matPag.pageIndex), nextPageNode);
      } else {
        if (index > this.rangeEnd && !dots[0]) {
          this.renderer.insertBefore(actionContainer, this.createText(this.pageGapTxt[0]), nextPageNode);
          dots[0] = true;
          break;
        }
        if (index < this.rangeEnd && !dots[1]) {
          this.renderer.insertBefore(actionContainer, this.createText(this.pageGapTxt[1]), nextPageNode);
          dots[1] = true;
        }
      }
    }
  }

  private shouldShowButton(index: number, totalPages: number) {
    return (index < this.showTotalPages && this.currentPage <= this.showTotalPages / 2) ||
      (index > this.rangeStart && index < this.rangeEnd) ||
      (index >= totalPages - this.showTotalPages && this.currentPage > totalPages - this.showTotalPages / 2);
  }

  private initNextAndLastButtons(prevButtonCount: number, actionContainer: any): void {
    if (prevButtonCount === 0) {
      const nodeArray = actionContainer.childNodes;
      setTimeout(() => this.updateButtonStyles(nodeArray));
    }
  }

  private updateButtonStyles(nodeArray: any) {
    for (const node of nodeArray) {
      if (node.nodeName !== 'BUTTON') {
        continue;
      }
      // Next Button styles
      this.updateButtonStyle(node);
    }
  }

  private updateButtonStyle(node: any) {
    if (node.innerHTML.length > 100) {
      this.toggleVisibility(node);
      if (node.disabled) {
        this.applyDisabledStyles(node);
      } else {
        this.applyEnabledStyles(node);
      }
    }
  }

  private toggleVisibility(node: HTMLElement) {
    if (this.matPag.length === 0) {
      this.renderer.addClass(node, 'd-none');
    } else {
      this.renderer.removeClass(node, 'd-none');
    }
  }

  private applyDisabledStyles(node: HTMLElement) {
    this.renderer.addClass(node, 'custom-paginator-arrow-disabled');
    this.renderer.removeClass(node, 'custom-paginator-arrow-enabled');
  }

  private applyEnabledStyles(node: HTMLElement) {
    this.renderer.addClass(node, 'custom-paginator-arrow-enabled');
    this.renderer.removeClass(node, 'custom-paginator-arrow-disabled');
  }

  private createText(text: string): HTMLSpanElement {
    const textSpan: HTMLSpanElement = this.renderer.createElement('span');
    this.renderer.addClass(textSpan, 'custom-paginator-ellipsis');
    this.renderer.appendChild(textSpan, this.renderer.createText(text));
    // Add span to private array for state
    this.buttons.push(textSpan);
    return textSpan;
  }

  private createButton(index: number, pageIndex: number): MatButton {
    const linkBtn: MatButton = this.renderer.createElement('button');
    this.renderer.setAttribute(linkBtn, 'class', 'custom-paginator-page');
    this.renderer.addClass(linkBtn, 'custom-paginator-page-enabled');
    const text = this.renderer.createText(index + 1 + '');
    this.renderer.addClass(linkBtn, 'mat-custom-page');
    switch (index) {
      case pageIndex:
        this.renderer.setAttribute(linkBtn, 'disabled', 'disabled');
        this.renderer.removeClass(linkBtn, 'custom-paginator-page-enabled');
        this.renderer.addClass(linkBtn, 'custom-paginator-page-disabled');
        break;
      default:
        this.renderer.listen(linkBtn, 'click', () => {
          this.switchPage(+index);
        });
        break;
    }
    this.renderer.appendChild(linkBtn, text);
    // Add button to private array for state
    this.buttons.push(linkBtn);
    return linkBtn;
  }

  /**
   * @description calculates the button range based on class input parameters and based on current page index value.
   */
  private initPageRange(): void {
    this.rangeStart = this.currentPage - this.showTotalPages / 2;
    this.rangeEnd = this.currentPage + this.showTotalPages / 2;
    this.buildPageNumbers();
  }

  private switchPage(index: number): void {
    this.matPag.pageIndex = index;
    this.matPag.page.emit({
      previousPageIndex: this.currentPage,
      pageIndex: index,
      pageSize: this.matPag.pageSize,
      length: this.matPag.length,
    });
    this.currentPage = index;
    this.initPageRange();
  }
}
